import { useMutation } from '@apollo/client'
import { UpdateNuggetElementInput } from '@deltek/specification-client/core/Models/nugget'
import { SetDrawingDesignation } from 'api/graphQL/mutations/projectElement.mutations'
import { useCallback } from 'react'
import { SERVER_DESIGNATION_PLACEHOLDER } from '../constants'

export interface UseDrawingDesignationMutationProps {
    onSuccess?: (designation: string) => void
    onError?: (error: Error) => void
}

export interface SaveDesignationInput {
    productTypeElementId: string
    projectId: string
    designation: string
    productFamilyId: string
}

/**
 * Hook for managing drawing designation mutations
 * Wraps the SetDrawingDesignation mutation with success/error handling
 */
export const useDrawingDesignationMutation = ({
    onSuccess,
    onError,
}: UseDrawingDesignationMutationProps = {}) => {

    const [setDrawingDesignationMutation, { loading }] = useMutation<
        { setDrawingDesignation: boolean },
        { input: Partial<UpdateNuggetElementInput> }
    >(SetDrawingDesignation, {
        onCompleted: (data) => {
            if (data.setDrawingDesignation) {
                onSuccess?.(data.setDrawingDesignation.toString())
            }
        },
        onError: (error) => {
            onError?.(error)
        },
    })

    const saveDesignation = useCallback(
        async ({
            productTypeElementId,
            projectId,
            designation,
            productFamilyId,
        }: SaveDesignationInput): Promise<void> => {
            const valueToSave = designation.trim() || SERVER_DESIGNATION_PLACEHOLDER

            await setDrawingDesignationMutation({
                variables: {
                    input: {
                        elementId: productTypeElementId,
                        projectId,
                        drawingDesignation: valueToSave,
                        productFamilyId,
                    },
                },
            })
        },
        [setDrawingDesignationMutation]
    )

    return {
        saveDesignation,
        isSaving: loading,
    }
}
